"use client";

import React from "react";
import { motion } from "framer-motion";
import { Testimonial } from "./testimonial";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";

function parseAntiAgencyHeadline(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(Commencez enfin à grandir)/i);
    return parts.map((part, i) => {
      if (/Commencez enfin à grandir/i.test(part)) {
        return <span key={i} className="italic text-gold">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(Start growing)/i);
  return parts.map((part, i) => {
    if (/Start growing/i.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parseAntiAgencySub(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(avant)/i);
    return parts.map((part, i) => {
      if (/avant/i.test(part)) {
        return <span key={i} className="italic text-gold">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(before)/i);
  return parts.map((part, i) => {
    if (/before/i.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parseBody3(text: string, lang: string) {
  if (lang === "fr") {
    const parts = text.split(/(Nous le remettons en question)/i);
    return parts.map((part, i) => {
      if (/Nous le remettons en question/i.test(part)) {
        return <span key={i} className="italic text-gold">{part}</span>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  }
  const parts = text.split(/(We challenge them)/i);
  return parts.map((part, i) => {
    if (/We challenge them/i.test(part)) {
      return <span key={i} className="italic text-gold">{part}</span>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export const AntiAgency = () => {
  const { t, lang } = useV2Translation();

  return (
    <section
      id="disruptor"
      data-testid="disruptor-section"
      className="relative py-24 lg:py-32 bg-paper-alt border-t border-line overflow-hidden"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 lg:self-start"
          >
            <h2
              data-testid="disruptor-headline"
              className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight"
            >
              {parseAntiAgencyHeadline(t.antiAgency.headline, lang)}
            </h2>
            <p className="font-mono text-sm mt-8 text-ink/70 max-w-[34ch]">
              {parseAntiAgencySub(t.antiAgency.subheadline, lang)}
            </p>
          </motion.div>

          <div className="col-span-12 lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.8,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="space-y-8 font-body text-lg lg:text-xl leading-[1.7] text-ink/85 max-w-[60ch]"
            >
              {/* Label: The Problem */}
              <span className="font-mono text-[10px] uppercase tracking-widest text-gold block">
                {t.antiAgency.labelProblem}
              </span>

              <p>{t.antiAgency.body1}</p>
              <p>{t.antiAgency.body2Intro}</p>

              {/* Option 2: bullet list */}
              <ul className="pl-0">
                {t.antiAgency.body2List.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 inline-block w-1.5 h-1.5 bg-gold shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {/* Label: Our Approach */}
              <span className="font-mono text-[10px] uppercase tracking-widest text-gold block">
                {t.antiAgency.labelApproach}
              </span>

              <p
                className="font-display text-3xl lg:text-5xl leading-tight tracking-tight text-ink not-italic"
                dangerouslySetInnerHTML={{ __html: t.antiAgency.body3 }}
              />

              {/* Option 1: short paragraphs */}
              {t.antiAgency.body4Parts.map((part, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: part }} />
              ))}
            </motion.div>

            <div id="stories" className="mt-16">
              <Testimonial
                index={1}
                quoteTitle={t.antiAgency.testimonial1.quoteTitle}
                body={t.antiAgency.testimonial1.body}
                author={t.antiAgency.testimonial1.author}
                role={t.antiAgency.testimonial1.role}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
